import Link from "next/link";
import { COUNCILS, getCouncilConfig } from "../../lib/councils";
import {
  SCOTTISH_COUNCIL_SEO_PROFILES,
  getScottishCouncilSeoProfile,
} from "../../lib/scottishCouncilSeo";

function topValue(applications, keys) {
  const counts = new Map();
  for (const application of applications || []) {
    const value = keys.map((key) => application?.[key]).find(Boolean);
    const clean = String(value || "").trim();
    if (!clean) continue;
    counts.set(clean, (counts.get(clean) || 0) + 1);
  }
  return [...counts.entries()].sort((a, b) => b[1] - a[1])[0]?.[0] || "Mixed applications";
}

function formatReferenceDate(value) {
  const date = value ? new Date(value) : null;
  if (!date || Number.isNaN(date.getTime())) return "Updated regularly";
  return new Intl.DateTimeFormat("en-GB", {
    day: "2-digit",
    month: "short",
    year: "numeric",
    timeZone: "UTC",
  }).format(date);
}

export function ScottishCouncilCoverageDirectory() {
  const liveSlugs = new Set(
    Object.values(COUNCILS)
      .filter((council) => council.publicEnabled)
      .map((council) => council.slug)
  );

  return (
    <details className="group border-t border-[var(--c-border)] py-4">
      <summary className="flex cursor-pointer list-none items-center justify-between gap-4 text-sm font-semibold text-[var(--c-text-primary)]">
        <span>Scottish council coverage</span>
        <span className="text-xs font-normal text-[var(--c-text-muted)] group-open:hidden">32 authorities</span>
        <span className="hidden text-xs font-normal text-[var(--c-text-muted)] group-open:inline">Close</span>
      </summary>
      <p className="mt-3 max-w-3xl text-sm leading-6 text-[var(--c-text-secondary)]">
        City Scope is structured for all 32 Scottish local authorities. Search links are published only when a council feed is live and data-backed.
      </p>
      <ul className="mt-4 grid gap-x-6 gap-y-2 text-sm sm:grid-cols-2 lg:grid-cols-4">
        {SCOTTISH_COUNCIL_SEO_PROFILES.map((profile) => {
          const isLive = liveSlugs.has(profile.slug);
          return (
            <li key={profile.slug} className="flex items-baseline justify-between gap-2 border-b border-[var(--c-border)] pb-2">
              {isLive ? (
                <Link className="font-medium text-[var(--c-text-primary)] hover:underline" href={`/planning-updates/${profile.shortCode}`}>
                  {profile.displayName}
                </Link>
              ) : (
                <span className="text-[var(--c-text-secondary)]">{profile.displayName}</span>
              )}
              <span className="shrink-0 text-[11px] uppercase text-[var(--c-text-muted)]">{isLive ? "Live" : "Planned"}</span>
            </li>
          );
        })}
      </ul>
    </details>
  );
}

export default function CouncilSearchGuide({
  councilSlug,
  applications = [],
  referenceDate = null,
}) {
  const council = getCouncilConfig(councilSlug);
  const profile = getScottishCouncilSeoProfile(council?.slug);
  if (!council || !profile || !council.publicEnabled) return null;

  const topCategory = topValue(applications, ["category", "application_category"]);
  const topStatus = topValue(applications, ["status", "application_status"]);
  const mapPath = `/planning-map/${council.shortCode}`;

  return (
    <section aria-labelledby="council-search-guide-title" className="mx-auto w-full max-w-6xl px-4 py-6">
      <div className="border-y border-[var(--c-border)] py-5">
        <p className="pr-kicker">Council search guide</p>
        <h2 id="council-search-guide-title" className="pr-heading mt-2 text-2xl">
          View {profile.displayName} planning applications
        </h2>
        <p className="mt-2 max-w-4xl text-sm leading-6 text-[var(--c-text-secondary)] sm:text-base">
          Search council records by address, postcode or reference, with clearer summaries, filters and map locations.
        </p>

        <div className="mt-4 flex flex-wrap gap-x-5 gap-y-2 text-sm font-medium">
          <Link className="text-[var(--c-text-primary)] hover:underline" href={mapPath}>
            View {profile.displayName} planning map
          </Link>
          <a className="text-[var(--c-text-primary)] hover:underline" href={council.publicSearchUrl} rel="noopener noreferrer" target="_blank">
            Official council planning portal
          </a>
        </div>

        <details className="group mt-4 border-t border-[var(--c-border)] pt-4">
          <summary className="cursor-pointer list-none text-sm font-semibold text-[var(--c-text-primary)]">
            Coverage and search help
          </summary>
          <div className="mt-3 text-sm leading-6 text-[var(--c-text-secondary)]">
            <p>
              Coverage includes {profile.places.join(", ")} and applications across the wider {profile.displayName} council area. City Scope organises public data from {profile.officialName} into summaries, key dates, mapped locations and dedicated application pages.
            </p>
            <dl className="mt-4 grid grid-cols-2 gap-x-6 gap-y-3 border-y border-[var(--c-border)] py-4 md:grid-cols-4">
              <div><dt className="text-xs text-[var(--c-text-muted)]">Latest snapshot</dt><dd className="font-semibold text-[var(--c-text-primary)]">{formatReferenceDate(referenceDate)}</dd></div>
              <div><dt className="text-xs text-[var(--c-text-muted)]">Common category</dt><dd className="font-semibold text-[var(--c-text-primary)]">{topCategory}</dd></div>
              <div><dt className="text-xs text-[var(--c-text-muted)]">Current status</dt><dd className="font-semibold text-[var(--c-text-primary)]">{topStatus}</dd></div>
            </dl>
            <div className="mt-4 grid gap-3 md:grid-cols-3">
              <p><strong className="text-[var(--c-text-primary)]">Find an application.</strong> Search a street, postcode, proposal keyword or planning reference, then refine by date, status, category or impact scale.</p>
              <p><strong className="text-[var(--c-text-primary)]">Read it clearly.</strong> Review summaries, source documents, key dates and mapped locations in one place.</p>
              <p><strong className="text-[var(--c-text-primary)]">Comment officially.</strong> Use the linked council record for formal representations and the definitive case status.</p>
            </div>
          </div>
        </details>
      </div>
    </section>
  );
}
